import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Stack,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText
} from '@mui/material';
import {
  Person as PersonIcon,
  PersonAdd as GuestIcon,
  AccessTime as TimeIcon,
  AttachMoney as MoneyIcon,
  Cancel as CancelIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Notes as NotesIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { coachingSessionAPI } from '../../services/api';
import { CoachingSession, CoachingSessionBooking } from '../../types/coaching';
import { formatTimeString } from '../../utils/dateUtils';

interface SessionBookingsViewProps {
  session: CoachingSession;
  onBookingUpdated?: () => void;
}

const SessionBookingsView: React.FC<SessionBookingsViewProps> = ({
  session,
  onBookingUpdated
}) => {
  const [bookings, setBookings] = useState<CoachingSessionBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancellingBookingId, setCancellingBookingId] = useState<string | null>(null);
  const [confirmCancelDialog, setConfirmCancelDialog] = useState<{
    open: boolean;
    booking: CoachingSessionBooking | null;
  }>({ open: false, booking: null });

  useEffect(() => {
    fetchBookings();
  }, [session.id]);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await coachingSessionAPI.getCoachingSessionBookings(session.id);
      setBookings(response.data);
    } catch (error: any) {
      console.error('Error fetching bookings:', error);
      
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to fetch bookings';
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelBooking = (booking: CoachingSessionBooking) => {
    setConfirmCancelDialog({ open: true, booking });
  };

  const confirmCancelBooking = async () => {
    if (!confirmCancelDialog.booking) return;

    try {
      setCancellingBookingId(confirmCancelDialog.booking.id);
      await coachingSessionAPI.cancelCoachingSessionBooking(session.id, confirmCancelDialog.booking.id);
      
      // Refresh bookings and notify parent
      await fetchBookings();
      onBookingUpdated?.();
      
      setConfirmCancelDialog({ open: false, booking: null });
    } catch (error: any) {
      console.error('Error cancelling booking:', error);
      
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to cancel booking';
      
      setError(errorMessage);
    } finally {
      setCancellingBookingId(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'CONFIRMED':
        return 'success';
      case 'CANCELLED':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatTime = (timeString: string) => {
    return formatTimeString(timeString);
  };

  const calculateTotalRevenue = () => {
    const confirmedBookings = bookings.filter(b => b.status === 'CONFIRMED');
    const total = confirmedBookings.reduce((sum, booking) => {
      const cost = booking.totalCost || 0;
      return sum + (typeof cost === 'number' ? cost : 0);
    }, 0);
    return total.toFixed(2);
  };

  const formatCurrency = (amount: number | string | undefined) => {
    if (!amount) return '0.00';
    const numericAmount = typeof amount === 'number' ? amount : parseFloat(amount.toString());
    return isNaN(numericAmount) ? '0.00' : numericAmount.toFixed(2);
  };

  const generateTimeSlots = () => {
    const startTime = new Date(`2000-01-01T${session.startTime}`);
    const endTime = new Date(`2000-01-01T${session.endTime}`);
    const slots = [];
    
    const current = new Date(startTime);
    while (current < endTime) {
      const slotEnd = new Date(current.getTime() + (session.minBookingDurationMinutes * 60 * 1000));
      
      // Check if this slot is booked
      const isBooked = bookings.some(booking => 
        booking.status === 'CONFIRMED' && 
        new Date(`2000-01-01T${booking.startTime}`) <= current &&
        new Date(`2000-01-01T${booking.endTime}`) > current
      );
      
      slots.push({
        startTime: current.toTimeString().slice(0, 5),
        endTime: slotEnd.toTimeString().slice(0, 5),
        isBooked
      });
      
      current.setMinutes(current.getMinutes() + session.minBookingDurationMinutes);
    }
    
    return slots;
  };

  const timeSlots = generateTimeSlots();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header with session summary */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 3 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Coach:</strong> {session.coachName}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Venues:</strong> {session.venues && session.venues.length > 0 ? `${session.venues.length} venue(s)` : 'Not specified'}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Bookings:</strong> {bookings.filter(b => b.status === 'CONFIRMED').length} / {session.capacity}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Revenue:</strong> ${calculateTotalRevenue()}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {/* Action Bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Bookings ({bookings.filter(b => b.status === 'CONFIRMED').length})
        </Typography>
        <Button
          startIcon={<RefreshIcon />}
          onClick={fetchBookings}
          disabled={loading}
          size="small"
        >
          Refresh
        </Button>
      </Box>

      {/* Timeline Visualization */}
      {/* <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            Session Timeline
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {timeSlots.map((slot, index) => (
              <Chip
                key={index}
                label={`${slot.startTime} - ${slot.endTime}`}
                color={slot.isBooked ? 'error' : 'success'}
                variant={slot.isBooked ? 'filled' : 'outlined'}
                size="small"
              />
            ))}
          </Box>
          <Box sx={{ display: 'flex', gap: 2, fontSize: '0.75rem' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box 
                sx={{ 
                  width: 12, 
                  height: 12, 
                  borderRadius: 0.5, 
                  border: 1,
                  borderColor: 'success.main',
                  bgcolor: 'transparent' 
                }} 
              />
              <Typography variant="caption">Available</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box 
                sx={{ 
                  width: 12, 
                  height: 12, 
                  borderRadius: 0.5, 
                  bgcolor: 'error.main' 
                }} 
              />
              <Typography variant="caption">Booked</Typography>
            </Box>
          </Box>
        </CardContent>
      </Card> */}

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Bookings List */}
      {bookings.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <PersonIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography variant="h6" color="text.secondary">
            No bookings yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This session hasn't been booked by anyone
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {bookings.map((booking) => (
            <Grid size={{ xs: 12 }} key={booking.id}>
              <Card 
                variant="outlined" 
                sx={{ 
                  position: 'relative',
                  opacity: booking.status === 'CANCELLED' ? 0.7 : 1,
                  borderColor: booking.status === 'CANCELLED' ? 'error.main' : 'divider',
                  '&::before': booking.status === 'CANCELLED' ? {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(128, 128, 128, 0.2)',
                    borderRadius: 'inherit',
                    zIndex: 1,
                    pointerEvents: 'none'
                  } : {}
                }}
              >
                <CardContent sx={{ position: 'relative', zIndex: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {booking.isGuestBooking ? (
                        <GuestIcon color={booking.status === 'CANCELLED' ? 'disabled' : 'action'} />
                      ) : (
                        <PersonIcon color={booking.status === 'CANCELLED' ? 'disabled' : 'action'} />
                      )}
                      <Typography 
                        variant="h6"
                        sx={{
                          color: booking.status === 'CANCELLED' ? 'text.disabled' : 'inherit',
                          textDecoration: booking.status === 'CANCELLED' ? 'line-through' : 'none'
                        }}
                      >
                        {booking.isGuestBooking ? booking.guestName : booking.memberName}
                      </Typography>
                      <Chip
                        label={booking.status}
                        color={getStatusColor(booking.status)}
                        size="small"
                      />
                    </Box>
                    
                    {booking.status === 'CONFIRMED' && (
                      <IconButton
                        color="error"
                        onClick={() => handleCancelBooking(booking)}
                        disabled={cancellingBookingId === booking.id}
                        size="small"
                      >
                        {cancellingBookingId === booking.id ? (
                          <CircularProgress size={20} />
                        ) : (
                          <CancelIcon />
                        )}
                      </IconButton>
                    )}
                  </Box>

                  <Grid container spacing={2}>
                    {/* Contact Info */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Stack spacing={1}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <EmailIcon 
                            fontSize="small" 
                            color={booking.status === 'CANCELLED' ? 'disabled' : 'action'} 
                          />
                          <Typography 
                            variant="body2"
                            color={booking.status === 'CANCELLED' ? 'text.disabled' : 'inherit'}
                          >
                            {booking.isGuestBooking ? booking.guestEmail : booking.memberEmail}
                          </Typography>
                        </Box>
                        
                        {booking.isGuestBooking && (
                          <Typography 
                            variant="caption" 
                            color={booking.status === 'CANCELLED' ? 'text.disabled' : 'text.secondary'}
                          >
                            Guest Booking
                          </Typography>
                        )}
                      </Stack>
                    </Grid>

                    {/* Booking Details */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Stack spacing={1}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TimeIcon 
                            fontSize="small" 
                            color={booking.status === 'CANCELLED' ? 'disabled' : 'action'} 
                          />
                          <Typography 
                            variant="body2"
                            color={booking.status === 'CANCELLED' ? 'text.disabled' : 'inherit'}
                          >
                            {formatTime(booking.startTime)} - {formatTime(booking.endTime)} ({booking.durationMinutes} min)
                          </Typography>
                        </Box>
                        
                        {booking.totalCost && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <MoneyIcon 
                              fontSize="small" 
                              color={booking.status === 'CANCELLED' ? 'disabled' : 'action'} 
                            />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography 
                                variant="body2"
                                color={booking.status === 'CANCELLED' ? 'text.disabled' : 'inherit'}
                                component="span"
                              >
                                ${formatCurrency(booking.totalCost)}
                              </Typography>
                              {booking.status !== 'CANCELLED' && booking.transactionStatus && (
                                <Chip 
                                  label={booking.transactionStatus}
                                  color={
                                    booking.transactionStatus === 'PAID' ? 'success' :
                                    booking.transactionStatus === 'PENDING' ? 'warning' :
                                    booking.transactionStatus === 'FAILED' || booking.transactionStatus === 'CANCELLED' ? 'error' :
                                    'default'
                                  }
                                  size="small" 
                                />
                              )}
                              {booking.status !== 'CANCELLED' && !booking.transactionStatus && !booking.paid && (
                                <Chip 
                                  label="Unpaid" 
                                  color="warning" 
                                  size="small" 
                                />
                              )}
                            </Box>
                          </Box>
                        )}
                      </Stack>
                    </Grid>

                    {/* Notes */}
                    {booking.notes && (
                      <Grid size={{ xs: 12 }}>
                        <Divider sx={{ my: 1 }} />
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          <NotesIcon 
                            fontSize="small" 
                            color={booking.status === 'CANCELLED' ? 'disabled' : 'action'} 
                          />
                          <Typography 
                            variant="body2" 
                            color={booking.status === 'CANCELLED' ? 'text.disabled' : 'text.secondary'}
                          >
                            {booking.notes}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                  </Grid>

                  {/* Booking metadata */}
                  <Box sx={{ mt: 2, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                    <Typography variant="caption" color="text.secondary">
                      Booked on {new Date(booking.createdAt).toLocaleDateString()} at {new Date(booking.createdAt).toLocaleTimeString()}
                      {booking.bookedByCoach && ' • Booked by coach'}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Cancel Confirmation Dialog */}
      <Dialog
        open={confirmCancelDialog.open}
        disableEscapeKeyDown={true}
      >
        <DialogTitle>Cancel Booking</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to cancel the booking for{' '}
            {confirmCancelDialog.booking?.isGuestBooking
              ? confirmCancelDialog.booking.guestName
              : confirmCancelDialog.booking?.memberName}?
            {'\n\n'}
            Time slot: {confirmCancelDialog.booking && formatTime(confirmCancelDialog.booking.startTime)} - {confirmCancelDialog.booking && formatTime(confirmCancelDialog.booking.endTime)}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setConfirmCancelDialog({ open: false, booking: null })}
          >
            Keep Booking
          </Button>
          <Button 
            onClick={confirmCancelBooking}
            color="error" 
            variant="contained"
            disabled={cancellingBookingId !== null}
          >
            {cancellingBookingId ? 'Cancelling...' : 'Cancel Booking'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SessionBookingsView;
